import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useSpeechSynthesis } from 'react-speech-kit';
import { convertNumberToWords, convertThaiToLao } from '../../../helpers';

// Styled components for better UI
const Container = styled.div`
  max-width: 100%;
  padding: 20px;
`;

const Title = styled.h2`
  color: #5a54a2;
  font-size: 22px;
  margin-bottom: 20px;
  text-align: center;
`;

const ProgressContainer = styled.div`
  margin-bottom: 15px;
`;

const ProgressBarStyled = styled.div`
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  margin: 10px 0;
  overflow: hidden;
`;

const Progress = styled.div`
  height: 100%;
  background-color: #5a54a2;
  border-radius: 4px;
  width: ${props => props.width}%;
  transition: width 0.1s ease;
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #666;
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
`;

const PlayButton = styled.button`
  background: #5a54a2;
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background: #4a4492;
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
  width: 70%;
`;

const VolumeIcon = styled.span`
  margin-right: 10px;
  font-size: 18px;
`;

const VolumeSlider = styled.input`
  flex: 1;
  height: 8px;
  -webkit-appearance: none;
  background: #ddd;
  outline: none;
  border-radius: 4px;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #5a54a2;
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #5a54a2;
    cursor: pointer;
    border: none;
  }
`;

const AudioList = styled.div`
  margin-top: 25px;
`;

const AudioItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 6px;
  background-color: ${props => props.active ? '#f0eeff' : 'transparent'};
  border: 1px solid ${props => props.active ? '#5a54a2' : '#eee'};
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f0eeff;
    border-color: #5a54a2;
  }
`;

const AudioName = styled.div`
  font-weight: ${props => props.active ? '500' : 'normal'};
  color: ${props => props.active ? '#5a54a2' : '#333'};
`;

const SettingAudioSound = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const { speak } = useSpeechSynthesis();

  
  const audioRef = useRef(null);
  
  useEffect(() => {
    // Set up event listeners
    const audio = audioRef.current;
    
    const setAudioData = () => {
      setDuration(audio.duration);
    };
    
    const setAudioTime = () => {
      setCurrentTime(audio.currentTime);
    };
    
    // Add event listeners
    audio.addEventListener('loadeddata', setAudioData);
    audio.addEventListener('timeupdate', setAudioTime);
    
    // Cleanup function to remove event listeners
    return () => {
      audio.removeEventListener('loadeddata', setAudioData);
      audio.removeEventListener('timeupdate', setAudioTime);
    };
  }, []);
  
  // Play/Pause functionality
  const togglePlay = () => {
    const audio = audioRef.current;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    
    setIsPlaying(!isPlaying);
  };
  
  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };
  
  // Handle seeking in the progress bar
  const handleProgressChange = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
  };
  
  // Format time in MM:SS
  const formatTime = (time) => {
    if (isNaN(time)) return '00:00';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Calculate progress percentage
  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;
  
  // Audio files with correct paths
  const audioFiles = [
    { id: 1, name: 'Product Empty', path: '/product_empty.ogg' },
    { id: 2, name: 'ສຽງຈາກຄອມພີວເຕີ', path: '/assets/audio/notic_v1.mp3', text: 'A01' },
    // { id: 3, name: 'Notification 2', path: '/assets/audio/notic_v2.mp3' },
    // { id: 4, name: 'Alternative Sound', path: '/aaa.ogg' }
  ];
  
  // Load voices when component mounts
  useEffect(() => {
    // Some browsers need this to load voices properly
    const loadVoices = () => {
      window.speechSynthesis.getVoices();
    };
    
    window.speechSynthesis.onvoiceschanged = loadVoices;
    loadVoices();
    
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);
  
  const [selectedAudio, setSelectedAudio] = useState(audioFiles[0]);
  const [speechRate, setSpeechRate] = useState(() => {
    return parseFloat(localStorage.getItem('speechRate') || '0.9');
  });
  
  // Update audio source when selected audio changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.src = selectedAudio.path;
      audioRef.current.load();
    }
  }, [selectedAudio]);

  // Helper function to get rate color
  function getRateColor(rate) {
    if (rate < 0.5) return "red";
    if (rate < 0.8) return "yellow";
    if (rate < 1.1) return "green";
    return "blue";
  }
  
  return (
    <Container>
      <Title>Audio Player</Title>
      <p>ສຽງສຳລັບຄອມພີວເຕີ</p>
        {/* Example rate control */}
        <select
            onChange={(e) => {
                const newRate = parseFloat(e.target.value);
                setSpeechRate(newRate);
                localStorage.setItem('speechRate', newRate);
            }}
            value={speechRate}
        >
            {[0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2].map((rate) => (
                <option key={rate} style={{ color: getRateColor(rate) }} value={rate}>
                    {`ຄວາມໄວ ${rate}`}
                </option>
            ))}
        </select>
      
      {/* Audio element */}
      <audio ref={audioRef} preload="metadata" />
      
      {/* Progress display */}
      <ProgressContainer>
        <ProgressBarStyled
          onClick={(e) => {
            const progressBar = e.currentTarget;
            const percent = e.nativeEvent.offsetX / progressBar.offsetWidth;
            audioRef.current.currentTime = percent * duration;
          }}
        >
          <Progress width={progressPercentage} />
        </ProgressBarStyled>
        
        <TimeDisplay>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </TimeDisplay>
      </ProgressContainer>
      
      {/* Controls */}
      <ControlsContainer>
        <PlayButton onClick={togglePlay}>
          {isPlaying ? '❚❚' : '►'}
        </PlayButton>
        
        <VolumeContainer>
          <VolumeIcon>🔊</VolumeIcon>
          <VolumeSlider
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={handleVolumeChange}
          />
        </VolumeContainer>
      </ControlsContainer>
      
      {/* Audio selection list */}
      <AudioList>
        {audioFiles.map(audio => (
          <AudioItem 
            key={audio.id} 
            active={selectedAudio.id === audio.id}
            onClick={() => {
              setIsPlaying(false);
              setSelectedAudio(audio);
            }}
          >
            <AudioName active={selectedAudio.id === audio.id}>
              {audio.name}
            </AudioName>
            
            <PlayButton 
              onClick={async (e) => {
                e.stopPropagation();
                
                // Special case for audio ID 2 - use text-to-speech
                if (audio.id === 2) {
                  // const speech = new SpeechSynthesisUtterance(audio.text || 'A011');
                  const voices = window.speechSynthesis.getVoices();
                  const thaiVoice = voices.find(voice => voice.lang === 'th-TH' || voice.lang === 'th');
                  const thaiText = convertThaiToLao(audio.text || 'A01');
                  // Get saved rate or use default
                  
                  if (thaiVoice) {
                    const savedRate = parseFloat(localStorage.getItem('speechRate')) || 0.9;
                    let responseText = `รหัส ${convertNumberToWords(thaiText)} หมดแล้วค่ะ`;
                    speak({ text: responseText, rate: savedRate, voice: thaiVoice });
                  }
                  
                  // // Get saved rate from localStorage or use default 0.9
                  // // const savedRate = parseFloat(localStorage.getItem('speechRate')) || 0.9;
                  // speech.rate = savedRate;
                  
                  // window.speechSynthesis.speak(speech);
                  return;
                }
                
                // For other audio files, use normal audio playback
                if (selectedAudio.id === audio.id) {
                  togglePlay();
                } else {
                  setSelectedAudio(audio);
                  setIsPlaying(true);
                  setTimeout(() => {
                    audioRef.current.play();
                  }, 100);
                }
              }}
              style={{ width: '30px', height: '30px' }}
            >
              {isPlaying && selectedAudio.id === audio.id ? '❚❚' : '►'}
            </PlayButton>
          </AudioItem>
        ))}
      </AudioList>
    </Container>
  );
};

export default SettingAudioSound;